import { useApolloClient } from "@apollo/client";
import { ApplyItemAction } from "@msys/apply-item-action-isomorphic";
import {
  assertNever,
  getDataOrNull,
  omit,
  resolveSingleArrayElement,
} from "@msys/common";
import { question } from "@msys/question-isomorphic";
import {
  CollapseSection,
  DataGrid,
  DebouncedSearchInput,
  GridColDef,
  LabeledValue,
  ListHeader,
  LoadingSpinner,
  Modal,
  ModalActions,
  ModalContent,
  ModalDialog,
  ModalOpenButton,
  ModalTitle,
  ModalWithSidePanelBaseContent,
  ModalWithSidePanelDialog,
  ModalWithSidePanelSections,
  Switch,
  useFormatting,
  useLocalStorageAsState,
  useScreenWidth,
} from "@msys/ui";
import {
  Add as AddIcon,
  Ballot as BallotIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  KeyboardDoubleArrowLeft as KeyboardDoubleArrowLeftIcon,
} from "@mui/icons-material";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import LinkIcon from "@mui/icons-material/Link";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  DialogActions,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Link as MuiLink,
  TextField as MuiTextField,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type { GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, FormikProps, FormikProvider, useFormik } from "formik";
import { uniqueId } from "lodash-es";
import { Moment } from "moment";
import { useSnackbar } from "notistack";
import React from "react";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import {
  AddressInput as AddressInputType,
  BuildingNode,
  CrmCompaniesFilters,
  Money,
  namedOperations,
  OrganisationCrmOrganisationsSorting,
  ProjectSourcePageName,
  ProjectType,
} from "../../../../clients/graphqlTypes.js";
import { RestrictedByCapabilityWithDebug } from "../../../auth/RestrictedByCapability.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../../auth/useUserData.js";
import { ButtonCreate } from "../../../commons/button/Button.js";
import { FullScreenToggleIconButton } from "../../../commons/button/FullScreenToggleIconButton.js";
import { SwitchCollectionViewButton } from "../../../commons/button/SwitchCollectionViewButton.js";
import { CheckboxField } from "../../../commons/form-fields/CheckboxField.js";
import { DatePickerField } from "../../../commons/form-fields/DatePickerField.js";
import { FormattedMoneyField } from "../../../commons/form-fields/FormattedMoneyField.js";
import { SelectField } from "../../../commons/form-fields/SelectField.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { FormikObserveState } from "../../../commons/FormikStateObserver.js";
import {
  CollectionView,
  useCollectionViewWithMobile,
} from "../../../commons/hooks/useCollectionView.js";
import { ConfirmModal } from "../../../commons/modals/ConfirmModal.js";
import { SelectedBoxWithIndicator } from "../../../commons/SelectedBoxWithIndicator.js";
import {
  PAGE_LIST_RESULTS_PER_PAGE,
  RESULTS_PER_PAGE_OPTIONS,
} from "../../../constants.js";
import {
  ALLOWED_VIEWS,
  CrmCompanyCollectionView,
} from "../../../main-routes/crm/CrmAll.js";
import { AddressField } from "../../addresses/AddressField.js";
import { getAddressLabel } from "../../addresses/helpers.js";
import { BuildingCreateForm } from "../../buildings/BuildingCreateForm.js";
import {
  BuildingRow__BuildingFragment,
  ModifyBuildingMutation,
  useModifyBuildingMutation,
} from "../../buildings/Buildings.generated.js";
import { CrmCompanyEditForm } from "../../crm-companies/CrmCompanyEditForm.js";
import { CrmCreateForm } from "../../crm/CrmCreateForm.js";
import { CrmContactType } from "../../crm/useCrmContactTypes.js";
import { CustomFieldConfigFragment } from "../../custom-fields/customFieldConfigs.generated.js";
import { useSetCustomFieldMutation } from "../../custom-fields/EditCustomFieldModal.generated.js";
import {
  CustomFieldsArray,
  CustomFieldsFormValues,
  getCustomFieldsInitialValues,
  getCustomFieldsSubmitValues,
  getCustomFieldsValidationSchema,
} from "../../custom-fields/EditCustomFieldModal.js";
import { useCustomFieldConfig } from "../../custom-fields/useCustomFieldConfig.js";
import {
  applyItemActionToApplyItemActionInput,
  WizardSection,
} from "../../quotes/WizardSection.js";
import { useHasSapS4HanaIntegration } from "../../sap-s4-hana/hooks.js";
import { resolveProjectCreationTeams } from "../../teams/useTeamCreationResolver.js";
import { searchQuotesSortingToQuoteTemplatesSorting } from "../../templates/helpers.js";
import {
  DEFAULT_FILTERS,
  DEFAULT_SORTING,
  TemplateBase,
  TemplatesDisplay,
} from "../../templates/quote/TemplatesDisplay.js";
import { useTemplatesQueryVariablesInState } from "../../templates/quote/useTemplatesQueryVariables.js";
import { useDataGridStateStore } from "../../users/useDataGridStateStore.js";
import { useCreateProjectMutation } from "../Projects.generated.js";
import { useProjectTypes } from "../useProjectTypes.js";
import {
  CrmCompany_BuildingFragment,
  useCreateProjectModal_BuildingsQuery,
  useCreateProjectModal_CreateQuoteMutation,
  useCreateProjectModal_CrmCompaniesQuery,
  useCreateProjectModal_OrganisationDefaultsQuery,
  useProjectAssignToMe_SetProjectAssigneeMutation,
  useProjectAssignToMeLazyQuery,
} from "./CreateProjectModal.generated.js";

export type CreateProjectInitialStatus = "opportunity" | "contracted";

type SidePanelState =
  | { status: "closed" }
  | { status: "client-select" }
  | { status: "client-create" }
  | { status: "client-edit" }
  | { status: "building-select" }
  | { status: "building-create" }
  | { status: "building-details" }
  | { status: "template-select"; selectedTemplate: TemplateBase | null }
  | { status: "template-wizard" };

interface CrmCompany {
  id: string;
  title: string;
}

interface FormValues extends CustomFieldsFormValues {
  title: string;
  description: string;
  type: ProjectType | null;
  crmOrganisation: CrmCompany | null;
  building: CrmCompany_BuildingFragment | null;
  buildingItemId: string | null;
  urgent: boolean;
  earliestStart: Moment | null;
  deadline: Moment | null;
  budgetAmount?: Money;
  initialStatus: CreateProjectInitialStatus;
  template: TemplateBase | null;
  templateWizardAnswerResults: ApplyItemAction[] | null;
  teamIds: string[];
}

interface Props {
  title?: string;
  crmOrganisation?: CrmCompany;
  building?: CrmCompany_BuildingFragment;
  buildingItemId?: string;
  pageName: ProjectSourcePageName;
  desiredProjectInitialStatus: CreateProjectInitialStatus;
  handleClose: () => void;
  handleComplete?: (projectId: string) => Promise<void> | void;
  refetchQueries?: string[];
}

export const CreateProjectModal = ({
  title,
  crmOrganisation,
  building,
  buildingItemId,
  pageName,
  desiredProjectInitialStatus,
  handleClose,
  handleComplete,
  refetchQueries,
}: Props) => {
  const viewer = useUserData().currentUser!;
  const { enqueueSnackbar } = useSnackbar();

  const { t } = useTranslate([
    "ProjectCreate",
    "TicketCreate",
    "Projects",
    "Global",
    "QuoteCreate",
  ]);

  const client = useApolloClient();

  const [assignedToMe, setAssignedToMe] = React.useState<boolean>(false);
  const { assignToMe, loading: assignToMeLoading } = useProjectAssignToMe({});

  const [sidePanelState, setSidePanelState] = React.useState<SidePanelState>({
    status: "closed",
  });

  const { customFieldConfig: customFieldConfigs, loading } =
    useCustomFieldConfig("Project");

  const [createProject, { loading: createProjectLoading }] =
    useCreateProjectMutation({ client });
  const [setCustomField, { loading: setCustomFieldLoading }] =
    useSetCustomFieldMutation({ client });

  const query = useCreateProjectModal_OrganisationDefaultsQuery({
    client,
    fetchPolicy: "cache-first",
  });
  const organisationDefaults = getDataOrNull(query.data?.organisationDefaults);
  const [createQuote] = useCreateProjectModal_CreateQuoteMutation({ client });

  const initialStatuses: CreateProjectInitialStatus[] = React.useMemo(
    () =>
      viewer.organisation.isCraftsmanOrganisation
        ? ["opportunity", "contracted"]
        : ["opportunity"],
    [viewer.organisation.isCraftsmanOrganisation]
  );

  const initialValues = React.useMemo(
    (): FormValues => ({
      title: t("My new project", { ns: "ProjectCreate" }),
      description: "",
      type: null,
      crmOrganisation: crmOrganisation ?? null,
      building: building ?? null,
      buildingItemId: buildingItemId ?? null,
      urgent: false,
      earliestStart: null,
      deadline: null,
      initialStatus: initialStatuses.includes(desiredProjectInitialStatus)
        ? desiredProjectInitialStatus
        : initialStatuses[0],
      customFields: getCustomFieldsInitialValues(
        customFieldConfigs.filter(f => f.showInCreateForm),
        []
      ),
      template: null,
      templateWizardAnswerResults: null,
      teamIds: (() => {
        const r = resolveProjectCreationTeams(viewer.teams, null);
        return r.kind === "resolved" ? r.teamIds : [];
      })(),
    }),
    [
      building,
      buildingItemId,
      crmOrganisation,
      customFieldConfigs,
      desiredProjectInitialStatus,
      initialStatuses,
      t,
      viewer.teams,
    ]
  );

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .label(t("Project title", { ns: "ProjectCreate" }))
      .required(),
    type: Yup.mixed<ProjectType | null>()
      .oneOf([
        "DIY",
        "NEW",
        "RENOVATION",
        "REPAIR",
        "MAINTENANCE",
        "OTHER",
        null,
      ])
      .nullable()
      .optional(),
    description: Yup.string().label(t("Description", { ns: "TicketCreate" })),
    earliestStart: Yup.date()
      .label(t("Earliest start", { ns: "TicketCreate" }))
      .nullable(),
    deadline: Yup.date()
      .label(t("Deadline", { ns: "TicketCreate" }))
      .nullable(),
    crmOrganisation: Yup.object()
      .shape({
        id: Yup.string(),
      })
      .nullable()
      .label(t("Client", { ns: "ProjectCreate" }))
      .when([], {
        is: () => viewer.organisation.capabilities.includes("QUOTING"),
        then: (schema: Yup.ObjectSchema<{ id: Yup.StringSchema }>) =>
          schema.required(),
      }),
    building: Yup.object().shape({ id: Yup.string() }).nullable(),
    buildingItemId: Yup.string().nullable(),
    customFields: getCustomFieldsValidationSchema(customFieldConfigs),
    template: Yup.object()
      .shape({
        id: Yup.string().required(),
        title: Yup.string().required(),
        resolvedAsReadModelVersionNumber: Yup.number().optional().nullable(),
      })
      .nullable(),
  });

  const handleSubmit = async (values: FormValues) => {
    if (
      viewer.organisation.capabilities.includes("QUOTING") &&
      !values.crmOrganisation
    ) {
      throw new Error("Client is mandatory");
    }

    const result = await createProject({
      variables: {
        input: {
          title: values.title,
          description: values.description,
          type: values.type ?? undefined,
          crmOrganisationId: values.crmOrganisation?.id ?? undefined,
          buildingItemId: values.buildingItemId,
          categories: [],
          urgent: values.urgent,
          earliestStart:
            values.earliestStart?.format("YYYY-MM-DD") || undefined,
          deadline: values.deadline?.format("YYYY-MM-DD") || undefined,
          budgetAmount: values.budgetAmount?.amount,
          pageName: pageName,
          desiredProjectInitialStatus: values.initialStatus,
          teamIds: values.teamIds,
        },
      },
      // TODO: instead of problems with refetchObservableQueries we are refetching explicitely. Fix if necessary!
      refetchQueries: [
        namedOperations.Query.PlanningProjects,
        namedOperations.Query.ProjectsList,
        ...(refetchQueries ?? []),
      ],
    });

    if (!result) return;

    const project = result.data!.createProject.project;
    const projectId = project.id;

    await setCustomField({
      variables: {
        input: {
          objectId: projectId,
          objectType: "Project",
          values: getCustomFieldsSubmitValues(
            customFieldConfigs,
            values.customFields
          ),
        },
      },
    });

    if (values.template) {
      const newQuote = await createQuote({
        variables: {
          input: {
            projectId,
            title: values.template.title,
            discountPercentage: 0,
            fromTemplate: {
              id: values.template.id,
              applyItemActions: values.templateWizardAnswerResults
                ? {
                    actions: values.templateWizardAnswerResults.map(
                      applyItemActionToApplyItemActionInput
                    ),
                    uuidSeed: null,
                  }
                : null,
            },
          },
        },
      });

      const docId = newQuote.data?.createQuote.quote.id;

      if (!docId) {
        throw new Error(
          t("Error while creating quote", {
            ns: "QuoteCreate",
          })
        );
      }
    }

    handleClose();
    await handleComplete?.(projectId);

    enqueueSnackbar(
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: "center",
        }}
      >
        <Typography>{t("Project created", { ns: "ProjectCreate" })}</Typography>
        <Button
          size="small"
          color="inherit"
          variant="outlined"
          disabled={assignedToMe || assignToMeLoading}
          onClick={async () => {
            await assignToMe(projectId);
            setAssignedToMe(true);
            enqueueSnackbar(t("Assigned to me", { ns: "ProjectCreate" }));
          }}
        >
          {t("Assign to me", { ns: "ProjectCreate" })}
        </Button>
      </Stack>
    );
  };

  const showSideContent = sidePanelState.status !== "closed";

  const formId = React.useMemo(() => uniqueId(), []);

  const handleCloseSidePanel = React.useCallback(() => {
    setSidePanelState({ status: "closed" });
  }, []);

  const { isMaxPhone } = useScreenWidth();

  const [isFullScreen, setIsFullScreen] = useLocalStorageAsState<boolean>(
    "msys-create-project-modal-fullscreen",
    false,
    true
  );

  return (
    <ModalWithSidePanelDialog
      fixedHeight={showSideContent}
      dialogProps={{
        maxWidth: showSideContent ? "xl" : "sm",
        ...(!isMaxPhone && isFullScreen ? { fullScreen: true } : undefined),
      }}
      handleClose={handleClose}
    >
      {loading ? (
        <ModalWithSidePanelBaseContent>
          <LoadingSpinner />
        </ModalWithSidePanelBaseContent>
      ) : (
        <Formik<FormValues>
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
          validateOnMount
        >
          {formikProps => (
            <ModalWithSidePanelSections
              fullScreen={!isMaxPhone && isFullScreen ? true : undefined}
              sideContent={
                sidePanelState.status === "client-select" ? (
                  <ClientSelectSidePanel
                    handleClose={handleCloseSidePanel}
                    handleSelect={client => {
                      formikProps.setFieldValue("crmOrganisation", client);
                      setSidePanelState({ status: "client-edit" });
                    }}
                    setSidePanelState={setSidePanelState}
                    buildingId={formikProps.values.building?.id}
                  />
                ) : sidePanelState.status === "client-create" ? (
                  <ClientCreateSidePanel
                    handleClose={handleCloseSidePanel}
                    handleComplete={client => {
                      formikProps.setFieldValue("crmOrganisation", client);
                      setSidePanelState({ status: "client-edit" });
                    }}
                  />
                ) : sidePanelState.status === "client-edit" ? (
                  <ClientEditSidePanel
                    handleClose={handleCloseSidePanel}
                    crmCompanyId={formikProps.values.crmOrganisation!.id}
                    handleChange={client => {
                      formikProps.setFieldValue("crmOrganisation", client);
                    }}
                  />
                ) : sidePanelState.status === "building-select" ? (
                  <LocationSelectSidePanel
                    crmCompanyId={formikProps.values.crmOrganisation?.id}
                    building={formikProps.values.building}
                    handleClose={handleCloseSidePanel}
                    handleSelect={(building, buildingItemId) => {
                      formikProps.setValues({
                        ...formikProps.values,
                        building,
                        buildingItemId,
                      });
                      setSidePanelState({ status: "building-details" });
                    }}
                    setSidePanelState={setSidePanelState}
                  />
                ) : sidePanelState.status === "building-create" ? (
                  <LocationCreateSidePanel
                    crmCompanyId={formikProps.values.crmOrganisation?.id}
                    handleClose={handleCloseSidePanel}
                    handleComplete={(building, buildingItemId) => {
                      formikProps.setValues({
                        ...formikProps.values,
                        building,
                        buildingItemId,
                      });
                      setSidePanelState({ status: "building-details" });
                    }}
                  />
                ) : sidePanelState.status === "building-details" ? (
                  <LocationDetailsSidePanel
                    building={formikProps.values.building!}
                    handleClose={handleCloseSidePanel}
                    handleComplete={building => {
                      formikProps.setValues({
                        ...formikProps.values,
                        building,
                        buildingItemId: building.rootItem.id,
                      });
                      setSidePanelState({ status: "closed" });
                    }}
                  />
                ) : sidePanelState.status === "template-select" ? (
                  <TemplateSelectSidePanel
                    handleClose={handleCloseSidePanel}
                    handleSelect={template => {
                      formikProps.setFieldValue("template", template);
                      formikProps.setFieldValue(
                        "templateWizardAnswerResults",
                        null
                      );
                      formikProps.setFieldValue(
                        "type",
                        resolveSingleArrayElement(template.typeOfProject)
                      );
                      setSidePanelState({ status: "template-wizard" });
                    }}
                  />
                ) : sidePanelState.status === "template-wizard" ? (
                  <TemplateWizardSidePanel
                    template={formikProps.values.template!}
                    initialAnswers={
                      formikProps.values.templateWizardAnswerResults ?? []
                    }
                    handleClose={handleCloseSidePanel}
                    handleAnswer={result => {
                      formikProps.setFieldValue(
                        "templateWizardAnswerResults",
                        result.map(r => r.answer) as ApplyItemAction[]
                      );
                    }}
                    handleComplete={() => {
                      setSidePanelState({ status: "closed" });
                    }}
                  />
                ) : sidePanelState.status === "closed" ? null : (
                  assertNever(sidePanelState)
                )
              }
              maxWidth={"sm"}
              maxWidthWithSideContent={"xl"}
            >
              <ModalTitle
                title={title ?? t("New project", { ns: "ProjectCreate" })}
                handleClose={handleClose}
                headerActions={
                  !isMaxPhone ? (
                    <FullScreenToggleIconButton
                      isFullScreen={isFullScreen}
                      setIsFullScreen={setIsFullScreen}
                    />
                  ) : undefined
                }
              />
              <ModalContent>
                <Form id={formId}>
                  <Stack spacing={2} direction="column">
                    <RestrictedByCapabilityWithDebug capability="QUOTING">
                      <ClientSection
                        formikProps={formikProps}
                        disableClientSelect={Boolean(crmOrganisation)}
                        sidePanelState={sidePanelState}
                        setSidePanelState={setSidePanelState}
                      />
                    </RestrictedByCapabilityWithDebug>

                    <ProjectDetailsSection
                      initialStatuses={initialStatuses}
                      formikProps={formikProps}
                      sidePanelState={sidePanelState}
                      setSidePanelState={setSidePanelState}
                    />
                    <AdditionalInfoSection
                      formikProps={formikProps}
                      customFieldConfigs={customFieldConfigs}
                    />
                  </Stack>
                </Form>
              </ModalContent>
              <ModalActions
                actionButtons={[
                  {
                    label: t("Cancel", { ns: "Global" }),
                    handleClick: handleClose,
                    buttonProps: {
                      variant: "text",
                      disabled: formikProps.isSubmitting,
                    },
                  },
                  {
                    label: t("Create project", { ns: "ProjectCreate" }),
                    buttonProps: {
                      type: "submit",
                      form: formId,
                      disabled: !formikProps.isValid,
                      loading: formikProps.isSubmitting,
                    },
                  },
                ]}
              />
            </ModalWithSidePanelSections>
          )}
        </Formik>
      )}
    </ModalWithSidePanelDialog>
  );
};

function ClientSection({
  formikProps,
  disableClientSelect,
  sidePanelState,
  setSidePanelState,
}: {
  formikProps: FormikProps<FormValues>;
  disableClientSelect: boolean;
  sidePanelState: SidePanelState;
  setSidePanelState: React.Dispatch<React.SetStateAction<SidePanelState>>;
}) {
  const { t } = useTranslate([
    "ProjectCreate",
    "TicketCreate",
    "CrmOrganisations",
    "Projects",
    "Buildings",
  ]);

  return (
    <CollapseSection
      title={t("Client details", {
        ns: "ProjectCreate",
      })}
      isInitiallyExpanded
    >
      <Stack spacing={1} direction="column">
        {!formikProps.values.crmOrganisation?.id ? (
          <Box
            sx={{
              width: "100%",
            }}
          >
            <SelectedBoxWithIndicator
              selected={sidePanelState.status === "client-select"}
            >
              <Button
                size="small"
                color="secondary"
                variant="text"
                startIcon={<AddIcon />}
                disabled={disableClientSelect || formikProps.isSubmitting}
                sx={{ textWrap: "nowrap" }}
                onClick={() =>
                  setSidePanelState({
                    status: "client-select",
                  })
                }
              >
                {t("Select client", { ns: "TicketCreate" })}
              </Button>
            </SelectedBoxWithIndicator>
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
            }}
          >
            <SelectedBoxWithIndicator
              selected={sidePanelState.status === "client-edit"}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <LabeledValue
                  label={t("Client", {
                    ns: "ProjectCreate",
                  })}
                >
                  <MuiLink
                    component={Link}
                    to={`/crm/organisations/${formikProps.values.crmOrganisation.id}`}
                    target="_blank"
                  >
                    {formikProps.values.crmOrganisation.title}
                  </MuiLink>
                </LabeledValue>

                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{
                    alignItems: "center",
                  }}
                >
                  <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_CRM">
                    <Tooltip title={t("Edit client", { ns: "ProjectCreate" })}>
                      <Box
                        sx={{
                          display: "flex",
                        }}
                      >
                        <IconButton
                          color="primary"
                          size="small"
                          disabled={formikProps.isSubmitting}
                          onClick={() =>
                            setSidePanelState({
                              status: "client-edit",
                            })
                          }
                        >
                          <EditIcon />
                        </IconButton>
                      </Box>
                    </Tooltip>
                  </RestrictedByOrganisationPermissionWithDebug>

                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => {
                      formikProps.setValues({
                        ...formikProps.values,
                        crmOrganisation: null,
                      });
                      setSidePanelState({
                        status: "closed",
                      });
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </SelectedBoxWithIndicator>
          </Box>
        )}

        {!formikProps.values.building?.id ||
        !formikProps.values.buildingItemId ? (
          <Box
            sx={{
              width: "100%",
            }}
          >
            <SelectedBoxWithIndicator
              selected={sidePanelState.status === "building-select"}
            >
              <Button
                size="small"
                color="secondary"
                variant="text"
                startIcon={<AddIcon />}
                disabled={formikProps.isSubmitting}
                sx={{ textWrap: "nowrap" }}
                onClick={() =>
                  setSidePanelState({
                    status: "building-select",
                  })
                }
              >
                {t("Select location", { ns: "TicketCreate" })}
              </Button>
            </SelectedBoxWithIndicator>
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
            }}
          >
            <SelectedBoxWithIndicator
              selected={
                sidePanelState.status === "building-details" ||
                sidePanelState.status === "building-select"
              }
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <LabeledValue
                  label={t("Location", {
                    ns: "Projects",
                  })}
                >
                  <MuiLink
                    component={Link}
                    to={`/buildings/${formikProps.values.building.id}`}
                    target="_blank"
                  >
                    {formikProps.values.building.buildingAddress
                      ? getAddressLabel(
                          formikProps.values.building.buildingAddress
                        )
                      : formikProps.values.building.id}
                  </MuiLink>
                </LabeledValue>

                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{
                    alignItems: "center",
                  }}
                >
                  <Tooltip title={t("Building details", { ns: "Buildings" })}>
                    <Box
                      sx={{
                        display: "flex",
                      }}
                    >
                      <IconButton
                        color="primary"
                        size="small"
                        disabled={formikProps.isSubmitting}
                        onClick={() =>
                          setSidePanelState({
                            status: "building-details",
                          })
                        }
                      >
                        <InfoOutlineIcon />
                      </IconButton>
                    </Box>
                  </Tooltip>

                  <Tooltip
                    title={t("Change location", { ns: "ProjectCreate" })}
                  >
                    <Box
                      sx={{
                        display: "flex",
                      }}
                    >
                      <IconButton
                        color="primary"
                        size="small"
                        disabled={formikProps.isSubmitting}
                        onClick={() =>
                          setSidePanelState({
                            status: "building-select",
                          })
                        }
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                  </Tooltip>

                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => {
                      formikProps.setValues({
                        ...formikProps.values,
                        building: null,
                        buildingItemId: null,
                      });
                      setSidePanelState(s => ({
                        status: "closed",
                      }));
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </SelectedBoxWithIndicator>
          </Box>
        )}

        {/*Excluded for now!*/}
        {/*<Autocomplete
        inputLabel={t("Placement", {
          ns: "TicketCreate",
        })}
        value={
          formikProps.values.clientId &&
          crmCompany?.id === formikProps.values.clientId
            ? crmCompany.buildings
                .find(building => building.id === formikProps.values.buildingId)
                ?.items?.find(
                  buildingItem =>
                    buildingItem.id === formikProps.values.buildingItemId
                ) ?? null
            : null
        }
        required
        disabled={
          formikProps.values.clientId && formikProps.values.buildingId
            ? formikProps.values.clientId.length === 0 &&
              formikProps.values.buildingId.length === 0
            : true
        }
        getOptionLabel={option => `${option.pathForPdf} ${option.title}`}
        options={
          formikProps.values.clientId &&
          crmCompany?.id === formikProps.values.clientId
            ? crmCompany.buildings.find(
                building => building.id === formikProps.values.buildingId
              )?.items ?? []
            : []
        }
        onChange={value => {
          const buildingItemId = value?.id ?? "";
          formikProps.setFieldValue("buildingItemId", buildingItemId);
        }}
      />*/}
      </Stack>
    </CollapseSection>
  );
}

function ProjectDetailsSection({
  formikProps,
  initialStatuses,
  sidePanelState,
  setSidePanelState,
}: {
  formikProps: FormikProps<FormValues>;
  initialStatuses: CreateProjectInitialStatus[];
  sidePanelState: SidePanelState;
  setSidePanelState: React.Dispatch<React.SetStateAction<SidePanelState>>;
}) {
  const { t } = useTranslate([
    "ProjectCreate",
    "TicketCreate",
    "Quote",
    "ProjectOverview",
    "ProjectTypes",
  ]);
  const viewer = useUserData().currentUser!;
  const { projectTypeOptions, projectTypeLabels } = useProjectTypes();
  const templateTypes = formikProps.values.template?.typeOfProject ?? [];
  return (
    <CollapseSection
      title={t("Project details", { ns: "ProjectCreate" })}
      isInitiallyExpanded={true}
    >
      <Stack direction="column" spacing={1}>
        <RestrictedByCapabilityWithDebug capability="QUOTING">
          {!formikProps.values.template?.id ? (
            <Box
              sx={{
                width: "100%",
              }}
            >
              <SelectedBoxWithIndicator
                selected={sidePanelState.status === "template-select"}
              >
                <Button
                  size="small"
                  color="secondary"
                  variant="text"
                  startIcon={<AddIcon />}
                  disabled={formikProps.isSubmitting}
                  sx={{ textWrap: "nowrap" }}
                  onClick={() =>
                    setSidePanelState({
                      status: "template-select",
                      selectedTemplate: null,
                    })
                  }
                >
                  {t("Add quote template", { ns: "ProjectCreate" })}
                </Button>
              </SelectedBoxWithIndicator>
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
              }}
            >
              <SelectedBoxWithIndicator
                selected={sidePanelState.status === "template-wizard"}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <LabeledValue
                    label={t("Quote template", {
                      ns: "ProjectCreate",
                    })}
                  >
                    <MuiLink
                      component={Link}
                      to={`/templates/documents/${formikProps.values.template.id}/${formikProps.values.template.resolvedAsReadModelVersionNumber}`}
                      target="_blank"
                    >
                      {formikProps.values.template.title}
                    </MuiLink>
                  </LabeledValue>

                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                      alignItems: "center",
                    }}
                  >
                    <Tooltip title={t("Open wizard", { ns: "Quote" })}>
                      <Box
                        sx={{
                          display: "flex",
                        }}
                      >
                        <IconButton
                          color="primary"
                          size="small"
                          disabled={formikProps.isSubmitting}
                          onClick={() =>
                            setSidePanelState({ status: "template-wizard" })
                          }
                        >
                          <BallotIcon />
                        </IconButton>
                      </Box>
                    </Tooltip>

                    <ModalOpenButton
                      Modal={ConfirmModal}
                      modalProps={{
                        title: t("Are you sure to remove this template?", {
                          ns: "ProjectCreate",
                        }),
                        text: t(
                          "Removing template will discard all previously answered template questions",
                          { ns: "ProjectCreate" }
                        ),
                        handleConfirm: () => {
                          formikProps.setValues({
                            ...formikProps.values,
                            ...(formikProps.values.title ===
                            formikProps.values.template?.title
                              ? {
                                  title: t("My new project", {
                                    ns: "ProjectCreate",
                                  }),
                                }
                              : undefined),
                            template: null,
                            templateWizardAnswerResults: null,
                            type: null,
                          });
                          setSidePanelState({ status: "closed" });
                        },
                      }}
                    >
                      <IconButton color="primary" size="small">
                        <DeleteIcon />
                      </IconButton>
                    </ModalOpenButton>
                  </Stack>
                </Stack>
              </SelectedBoxWithIndicator>
            </Box>
          )}
        </RestrictedByCapabilityWithDebug>

        <TextField
          name="title"
          label={t("Project title", { ns: "ProjectCreate" })}
          required
        />

        <RestrictedByCapabilityWithDebug capability="TEAMS">
          {viewer.teams.length > 1 && (
            <Autocomplete
              multiple
              size="small"
              options={viewer.teams}
              value={viewer.teams.filter(team =>
                formikProps.values.teamIds.includes(team.id)
              )}
              getOptionLabel={option => option.name}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              onChange={(_, selected) => {
                formikProps.setFieldValue(
                  "teamIds",
                  selected.map(team => team.id)
                );
              }}
              renderInput={params => (
                <MuiTextField
                  {...params}
                  label={t("Teams", { ns: "ProjectOverview" })}
                />
              )}
            />
          )}
        </RestrictedByCapabilityWithDebug>
        <SelectField
          name="type"
          options={[
            {
              value: "" as unknown as ProjectType,
              label: t("No project type", {
                ns: "ProjectTypes",
              }),
            },
            ...(templateTypes.length > 1
              ? projectTypeOptions.filter(option =>
                  templateTypes.includes(option.value)
                )
              : projectTypeOptions),
          ]}
          label={t("Type", { ns: "ProjectTypes" })}
          disabled={templateTypes.length === 1}
          onChange={value => {
            formikProps.setFieldValue(
              "type",
              value === ("" as unknown) ? null : value
            );
          }}
          renderValue={value =>
            value
              ? projectTypeLabels[value as ProjectType]
              : t("No project type", {
                  ns: "ProjectTypes",
                })
          }
        />
        {formikProps.values.template &&
          !formikProps.values.template.typeOfProject?.length && (
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                alignItems: "center",
              }}
            >
              <InfoOutlineIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("No project type defined by template", {
                  ns: "ProjectCreate",
                })}
              </Typography>
            </Stack>
          )}
        {templateTypes.length > 1 && (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              alignItems: "center",
            }}
          >
            <InfoOutlineIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
              }}
            >
              {t("Choose a project type from the template", {
                ns: "ProjectCreate",
              })}
            </Typography>
          </Stack>
        )}

        <Box>
          <Grid container spacing={1}>
            {initialStatuses.length > 0 && (
              <Grid size={12}>
                <FormControl variant="standard" fullWidth={false}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          formikProps.values.initialStatus === "contracted"
                        }
                        onChange={e => {
                          formikProps.setFieldValue(
                            "initialStatus",
                            e.currentTarget.checked
                              ? "contracted"
                              : "opportunity"
                          );
                        }}
                      />
                    }
                    label={t("Contract signed/agreed offline", {
                      ns: "ProjectCreate",
                    })}
                  />
                </FormControl>
              </Grid>
            )}
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <CheckboxField
                label={t("Urgent", { ns: "TicketCreate" })}
                name="urgent"
              />
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </CollapseSection>
  );
}

function AdditionalInfoSection({
  formikProps,
  customFieldConfigs,
}: {
  formikProps: FormikProps<FormValues>;
  customFieldConfigs: CustomFieldConfigFragment[];
}) {
  const { t } = useTranslate(["ProjectCreate", "TicketCreate"]);
  return (
    <CollapseSection
      title={t("Additional info", {
        ns: "ProjectCreate",
      })}
      isInitiallyExpanded={false}
    >
      <Stack direction="column" spacing={1}>
        <TextField
          name="description"
          label={t("Description", { ns: "TicketCreate" })}
          multiline
        />
        <FormattedMoneyField
          name="budgetAmount"
          label={t("Budget", { ns: "TicketCreate" })}
          type="money"
        />

        <Stack direction="row" spacing={1}>
          <DatePickerField
            label={t("Earliest start", { ns: "TicketCreate" })}
            name="earliestStart"
          />
          <DatePickerField
            label={t("Deadline", { ns: "TicketCreate" })}
            name="deadline"
          />
        </Stack>

        {customFieldConfigs.length > 0 && (
          <CustomFieldsArray
            values={formikProps.values}
            isSubmitting={formikProps.isSubmitting}
            setFieldValue={formikProps.setFieldValue}
            customFieldConfigs={customFieldConfigs}
            objectType="Project"
            useAutoSave={false}
          />
        )}
      </Stack>
    </CollapseSection>
  );
}

function ClientSelectSidePanel({
  handleSelect,
  handleClose,
  setSidePanelState,
  buildingId,
}: {
  handleSelect: (client: CrmCompany) => void;
  handleClose: () => void;
  setSidePanelState: (state: SidePanelState) => void;
  buildingId?: string;
}) {
  const hasSapS4HanaIntegration = useHasSapS4HanaIntegration();

  const { currentUser } = useUserData();

  const projectClientFilters =
    currentUser?.organisation?.configurations?.projectClientFilters;

  const { isMinDesktop, isMinTablet } = useScreenWidth();
  const { t } = useTranslate(["TicketCreate", "Global", "CrmOrganisations"]);

  const [activeView, setActiveView] =
    useCollectionViewWithMobile<CollectionView>(
      "crm-companies",
      "table",
      "list"
    );

  const [paginationModel, setPaginationModel] = React.useState<{
    page: number;
    pageSize: number;
  }>({
    page: 0,
    pageSize: PAGE_LIST_RESULTS_PER_PAGE,
  });
  const [sorting, setSorting] = React.useState<
    OrganisationCrmOrganisationsSorting[]
  >([
    {
      column: "organisationName",
      direction: "asc",
    },
  ]);
  const [filters, setFilters] = React.useState<
    Omit<
      CrmCompaniesFilters,
      "businessPartnerRolesAll" | "businessPartnerRolesExclude" | "tagsExclude"
    >
  >({});
  const [searchTerm, setSearchTerm] = React.useState<string>("");

  const client = useApolloClient();
  const query = useCreateProjectModal_CrmCompaniesQuery({
    client,
    variables: {
      limit: paginationModel.pageSize,
      offset: paginationModel.pageSize * paginationModel.page,
      sorting,
      searchTerm,
      filters: {
        ...filters,
        businessPartnerRolesAll: projectClientFilters?.businessPartnerRolesAll,
        businessPartnerRolesExclude:
          projectClientFilters?.businessPartnerRolesExclude,
        tagsExclude: projectClientFilters?.tagsExclude,
      },
    },
  });
  const totalCount =
    getDataOrNull((query.data ?? query.previousData)?.crmCompanies)
      ?.totalCount ?? 0;
  const crmCompanies =
    getDataOrNull((query.data ?? query.previousData)?.crmCompanies)?.edges.map(
      edge => edge.node
    ) ?? [];

  const [selectedClient, setSelectedClient] = React.useState<CrmCompany | null>(
    null
  );

  const sideClientSelect = (
    <Stack
      sx={{
        height: "100%",
      }}
    >
      <ListHeader
        SwitchViewButton={
          <SwitchCollectionViewButton
            allowedViews={ALLOWED_VIEWS}
            activeView={activeView}
            setActiveView={setActiveView}
          />
        }
        SearchField={
          <DebouncedSearchInput
            placeholder={t("Search", {
              ns: "Global",
            })}
            defaultValue={searchTerm}
            onChangeSearchTerm={newValue => setSearchTerm(newValue)}
          />
        }
        FilterFields={
          buildingId && isMinTablet ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: 0,
                pl: 1.5,
              }}
            >
              <Switch
                label={t("Linked to location only", { ns: "CrmOrganisations" })}
                checked={Boolean(filters.linkedToBuildingId)}
                onChange={(_, checked) => {
                  setFilters(filters => {
                    if (checked) {
                      return {
                        ...filters,
                        linkedToBuildingId: buildingId,
                      };
                    } else {
                      return {
                        ...filters,
                        linkedToBuildingId: undefined,
                      };
                    }
                  });
                }}
              />
            </Box>
          ) : undefined
        }
        sx={{ marginBottom: 2 }}
      />
      <CrmCompanyCollectionView
        activeView={activeView}
        isLoading={query.loading}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        sorting={sorting}
        setSorting={setSorting}
        crmCompanies={crmCompanies}
        totalCount={totalCount}
        selectedCrmCompany={selectedClient}
        setSelectedCrmCompany={setSelectedClient}
      />
    </Stack>
  );

  const headerActions = !hasSapS4HanaIntegration && (
    <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_CRM">
      <ButtonCreate
        variant="contained"
        color="primary"
        title={t("New contact", {
          ns: "CrmOrganisations",
        })}
        onClick={() => {
          setSidePanelState({ status: "client-create" });
        }}
      />
    </RestrictedByOrganisationPermissionWithDebug>
  );

  return isMinDesktop ? (
    <>
      <ModalTitle
        handleClose={handleClose}
        title={t("Select client", { ns: "TicketCreate" })}
        headerActions={headerActions}
      />
      <ModalContent>{sideClientSelect}</ModalContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <IconButton color="primary" onClick={handleClose}>
          <KeyboardDoubleArrowLeftIcon />
        </IconButton>
        <Button
          color="primary"
          variant="contained"
          disabled={!selectedClient}
          onClick={() => {
            if (!selectedClient) {
              throw new Error("No client selected");
            }
            handleSelect(selectedClient);
          }}
        >
          {t("Select", { ns: "Global" })}
        </Button>
      </DialogActions>
    </>
  ) : (
    <Modal
      handleClose={handleClose}
      title={t("Select client", { ns: "TicketCreate" })}
      headerActions={headerActions}
      actionButtons={[
        {
          label: t("Cancel", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text" },
        },
        {
          label: t("Select", { ns: "Global" }),
          handleClick: () => {
            if (!selectedClient) {
              throw new Error("No client selected");
            }
            handleSelect(selectedClient);
          },
          buttonProps: { disabled: !selectedClient },
        },
      ]}
    >
      {sideClientSelect}
    </Modal>
  );
}

const ALLOWED_CONTACT_TYPES = [
  "COMPANY",
  "INDIVIDUAL",
] satisfies CrmContactType[];

function ClientCreateSidePanel({
  handleClose,
  handleComplete,
}: {
  handleClose: () => void;
  handleComplete: (client: CrmCompany) => void;
}) {
  const { isMinDesktop } = useScreenWidth();
  const { t } = useTranslate(["TicketCreate", "Global", "CrmOrganisations"]);

  const formId = React.useMemo(() => uniqueId(), []);
  const [formikState, setFormikState] =
    React.useState<FormikObserveState | null>(null);

  const crmCreateForm = (
    <CrmCreateForm
      formId={formId}
      handleFormikObserveStateChange={setFormikState}
      handleComplete={handleComplete}
      allowedContactTypes={ALLOWED_CONTACT_TYPES}
    />
  );

  return isMinDesktop ? (
    <>
      <ModalTitle
        handleClose={handleClose}
        title={t("Create client", { ns: "CrmOrganisations" })}
      />
      <ModalContent>{crmCreateForm}</ModalContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <IconButton color="primary" onClick={handleClose}>
          <KeyboardDoubleArrowLeftIcon />
        </IconButton>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          form={formId}
          disabled={
            !formikState?.dirty ||
            formikState?.isSubmitting ||
            formikState?.isValidating
          }
          loading={formikState?.isSubmitting || formikState?.isValidating}
        >
          {t("Create", { ns: "Global" })}
        </Button>
      </DialogActions>
    </>
  ) : (
    <Modal
      handleClose={handleClose}
      title={t("Create client", { ns: "CrmOrganisations" })}
      actionButtons={[
        {
          label: t("Cancel", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text" },
        },
        {
          label: t("Select", { ns: "Global" }),
          buttonProps: {
            type: "submit",
            form: formId,
            disabled:
              !formikState?.dirty ||
              formikState?.isSubmitting ||
              formikState?.isValidating,
            loading: formikState?.isSubmitting || formikState?.isValidating,
          },
        },
      ]}
    >
      {crmCreateForm}
    </Modal>
  );
}

function ClientEditSidePanel({
  crmCompanyId,
  handleClose,
  handleChange,
}: {
  crmCompanyId: string;
  handleClose: () => void;
  handleChange: (client: CrmCompany) => void;
}) {
  const { isMinDesktop } = useScreenWidth();
  const { t } = useTranslate(["ProjectCreate", "Global"]);

  const title = t("Edit client", { ns: "ProjectCreate" });
  const crmEditForm = (
    <CrmCompanyEditForm
      crmCompanyId={crmCompanyId}
      onCrmCompanyChange={handleChange}
    />
  );

  return isMinDesktop ? (
    <>
      <ModalTitle handleClose={handleClose} title={title} />
      <ModalContent>{crmEditForm}</ModalContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <IconButton color="primary" onClick={handleClose}>
          <KeyboardDoubleArrowLeftIcon />
        </IconButton>
      </DialogActions>
    </>
  ) : (
    <Modal
      handleClose={handleClose}
      title={title}
      actionButtons={[
        {
          label: t("Close", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text" },
        },
      ]}
    >
      {crmEditForm}
    </Modal>
  );
}

function LocationSelectSidePanel({
  crmCompanyId,
  building,
  handleSelect,
  handleClose,
  setSidePanelState,
}: {
  crmCompanyId?: string;
  building?: CrmCompany_BuildingFragment | null;
  handleSelect: (
    building: CrmCompany_BuildingFragment,
    buildingItemId: string
  ) => void;
  handleClose: () => void;
  setSidePanelState: (state: SidePanelState) => void;
}) {
  const { isMinDesktop } = useScreenWidth();
  const { t } = useTranslate(["ProjectCreate", "TicketCreate", "Global"]);

  const [selectedBuilding, setSelectedBuilding] =
    React.useState<CrmCompany_BuildingFragment | null>(building ?? null);

  const buildingsCollectionView = (
    <BuildingsCollectionView
      crmCompanyId={crmCompanyId}
      selectedBuilding={selectedBuilding}
      setSelectedBuilding={setSelectedBuilding}
    />
  );

  const headerActions = (
    <ButtonCreate
      variant="contained"
      color="primary"
      title={t("New location", {
        ns: "ProjectCreate",
      })}
      onClick={() => {
        setSidePanelState({ status: "building-create" });
      }}
    />
  );

  return isMinDesktop ? (
    <>
      <ModalTitle
        handleClose={handleClose}
        title={t("Select location", { ns: "TicketCreate" })}
        headerActions={headerActions}
      />
      <ModalContent>{buildingsCollectionView}</ModalContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <IconButton color="primary" onClick={handleClose}>
          <KeyboardDoubleArrowLeftIcon />
        </IconButton>
        <Button
          color="primary"
          variant="contained"
          disabled={!selectedBuilding}
          onClick={() => {
            if (!selectedBuilding) {
              throw new Error("No building selected");
            }
            handleSelect(selectedBuilding, selectedBuilding.rootItem.id);
          }}
        >
          {t("Select", { ns: "Global" })}
        </Button>
      </DialogActions>
    </>
  ) : (
    <Modal
      handleClose={handleClose}
      title={t("Select location", { ns: "TicketCreate" })}
      headerActions={headerActions}
      actionButtons={[
        {
          label: t("Cancel", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text" },
        },
        {
          label: t("Select", { ns: "Global" }),
          handleClick: () => {
            if (!selectedBuilding) {
              throw new Error("No building selected");
            }
            handleSelect(selectedBuilding, selectedBuilding.rootItem.id);
          },
          buttonProps: { disabled: !selectedBuilding },
        },
      ]}
    >
      {buildingsCollectionView}
    </Modal>
  );
}

function BuildingsCollectionView({
  crmCompanyId,
  selectedBuilding,
  setSelectedBuilding,
}: {
  crmCompanyId?: string;
  selectedBuilding: CrmCompany_BuildingFragment | null;
  setSelectedBuilding: (building: CrmCompany_BuildingFragment) => void;
}) {
  const { getFormattedDate } = useFormatting();
  const { t } = useTranslate(["Buildings", "Global"]);

  const [paginationModel, setPaginationModel] = React.useState<{
    page: number;
    pageSize: number;
  }>({
    page: 0,
    pageSize: PAGE_LIST_RESULTS_PER_PAGE,
  });

  const [searchTerm, setSearchTerm] = React.useState("");

  const client = useApolloClient();
  const buildingsQuery = useCreateProjectModal_BuildingsQuery({
    client,
    variables: {
      offset: paginationModel.page * paginationModel.pageSize,
      limit: paginationModel.pageSize,
      crmCompanyId,
      searchTerm,
    },
  });

  const buildings = React.useMemo(
    () =>
      buildingsQuery.data?.buildings.edges.map(edge => ({
        ...edge.node.building,
        isLinked: edge.node.isLinked,
      })) ?? [],
    [buildingsQuery.data]
  );
  const total = buildingsQuery.data?.buildings.totalCount ?? 0;

  const stateStore = useDataGridStateStore("Buildings");

  const rowSelectionModel = React.useMemo(
    () =>
      ({
        type: "include",
        ids: new Set(selectedBuilding ? [selectedBuilding.id] : []),
      }) satisfies GridRowSelectionModel,
    [selectedBuilding]
  );

  return (
    <Stack
      sx={{
        height: "100%",
      }}
    >
      <ListHeader
        SearchField={
          <DebouncedSearchInput
            placeholder={t("Search", { ns: "Global" })}
            defaultValue={searchTerm}
            onChangeSearchTerm={searchTerm => {
              setSearchTerm(searchTerm);
            }}
          />
        }
        sx={{ marginBottom: 2 }}
      />
      <DataGrid<CrmCompany_BuildingFragment & Pick<BuildingNode, "isLinked">>
        stateStore={stateStore}
        loading={buildingsQuery.loading}
        hideFooter={total === 0}
        columns={[
          ...(crmCompanyId
            ? [
                {
                  field: "isLinked",
                  headerName: "",
                  width: 40,
                  minWidth: 40,
                  resizable: false,
                  disableColumnMenu: true,
                  sortable: false,
                  renderCell: ({ row: building }) =>
                    building.isLinked ? (
                      <Tooltip title={t("Linked", { ns: "Buildings" })}>
                        <LinkIcon
                          color="secondary"
                          sx={{
                            fontSize: "small",
                          }}
                        />
                      </Tooltip>
                    ) : null,
                } as GridColDef<
                  CrmCompany_BuildingFragment & Pick<BuildingNode, "isLinked">
                >,
              ]
            : []),
          {
            field: "title",
            headerName: t("Title", {
              ns: "Buildings",
            }),
            flex: 1,
            minWidth: 100,
            sortable: false,
            renderCell: ({ row: building }) => building.title,
          },
          {
            field: "address",
            headerName: t("Address", {
              ns: "Buildings",
            }),
            flex: 1,
            minWidth: 100,
            sortable: false,
            renderCell: ({ row: building }) =>
              getAddressLabel(building.buildingAddress),
          },
          {
            field: "createdAt",
            headerName: t("Created", {
              ns: "Buildings",
            }),
            flex: 1,
            minWidth: 100,
            sortable: false,
            renderCell: ({ row: building }) =>
              getFormattedDate(building.createdAt),
          },
        ]}
        paginationModel={paginationModel}
        onPaginationModelChange={newPaginationModel => {
          setPaginationModel(newPaginationModel);
        }}
        disableColumnFilter
        pageSizeOptions={RESULTS_PER_PAGE_OPTIONS}
        rows={buildings}
        rowCount={total}
        rowSelectionModel={rowSelectionModel}
        onRowClick={({ row: building }) => {
          setSelectedBuilding(
            selectedBuilding?.id === building.id ? null : building
          );
        }}
      />
    </Stack>
  );
}

function LocationCreateSidePanel({
  crmCompanyId,
  handleClose,
  handleComplete,
}: {
  crmCompanyId?: string;
  handleClose: () => void;
  handleComplete: (
    building: BuildingRow__BuildingFragment,
    buildingRootItemId: string
  ) => void;
}) {
  const { isMinDesktop } = useScreenWidth();
  const { t } = useTranslate(["ProjectCreate", "BuildingCreate", "Global"]);

  const formId = React.useMemo(() => uniqueId(), []);
  const [formikState, setFormikState] =
    React.useState<FormikObserveState | null>(null);

  const buildingCreateForm = (
    <BuildingCreateForm
      crmOrganisationId={crmCompanyId}
      formId={formId}
      handleFormikObserveStateChange={setFormikState}
      handleComplete={(building, buildingRootItemId) => {
        handleComplete(building, buildingRootItemId);
      }}
      hideTitle
      hideTemplate
    />
  );

  return isMinDesktop ? (
    <>
      <ModalTitle
        handleClose={handleClose}
        title={t("Create building", { ns: "BuildingCreate" })}
      />
      <ModalContent>{buildingCreateForm}</ModalContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <IconButton color="primary" onClick={handleClose}>
          <KeyboardDoubleArrowLeftIcon />
        </IconButton>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          form={formId}
          disabled={!formikState?.dirty || !formikState?.isValid}
          loading={formikState?.isSubmitting}
        >
          {t("Create", { ns: "Global" })}
        </Button>
      </DialogActions>
    </>
  ) : (
    <Modal
      handleClose={handleClose}
      title={t("Create building", { ns: "BuildingCreate" })}
      actionButtons={[
        {
          label: t("Cancel", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text" },
        },
        {
          label: t("Create", { ns: "Global" }),
          buttonProps: {
            type: "submit",
            form: formId,
            disabled: !formikState?.dirty || !formikState?.isValid,
            loading: formikState?.isSubmitting,
          },
        },
      ]}
    >
      {buildingCreateForm}
    </Modal>
  );
}

function LocationDetailsSidePanel({
  building,
  handleClose,
  handleComplete,
}: {
  building: CrmCompany_BuildingFragment;
  handleClose: () => void;
  handleComplete: (
    building: ModifyBuildingMutation["modifyBuilding"]["building"]
  ) => void;
}) {
  const { isMinDesktop } = useScreenWidth();
  const { t } = useTranslate(["Buildings", "BuildingDetails", "Global"]);
  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();
  const [modifyBuilding] = useModifyBuildingMutation({ client });

  const handleConfirm = async ({
    buildingAddress,
  }: {
    buildingAddress: AddressInputType | null | undefined;
  }) => {
    if (!buildingAddress) {
      throw new Error("Address is required");
    }

    try {
      const result = await modifyBuilding({
        variables: {
          input: {
            id: building.id,
            values: {
              buildingAddress,
            },
          },
        },
      });

      if (!result.data?.modifyBuilding.building) {
        throw new Error("Failed to modify building");
      }

      handleComplete(result.data?.modifyBuilding.building);
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  const formik = useFormik({
    initialValues: {
      buildingAddress: building.buildingAddress
        ? {
            ...omit(building.buildingAddress, "id", "__typename"),
            location: building.buildingAddress?.location
              ? omit(building.buildingAddress.location, "__typename")
              : null,
          }
        : null,
    },
    validationSchema: Yup.object().shape({
      buildingAddress: Yup.object().nullable().required(),
    }),
    onSubmit: handleConfirm,
  });
  const formId = React.useMemo(() => uniqueId(), []);

  const title = t("Building details", { ns: "Buildings" });

  const form = (
    <FormikProvider value={formik}>
      <Form id={formId}>
        <AddressField
          label={t("Location", { ns: "BuildingDetails" })}
          name="buildingAddress"
          disabled
          showMap
        />
      </Form>
    </FormikProvider>
  );

  return isMinDesktop ? (
    <>
      <ModalTitle handleClose={handleClose} title={title} />
      <ModalContent>{form}</ModalContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <IconButton color="primary" onClick={handleClose}>
          <KeyboardDoubleArrowLeftIcon />
        </IconButton>
      </DialogActions>
    </>
  ) : (
    <Modal
      handleClose={handleClose}
      title={title}
      actionButtons={[
        {
          label: t("Close", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text" },
        },
      ]}
    >
      {form}
    </Modal>
  );
}

function TemplateSelectSidePanel({
  handleClose,
  handleSelect,
}: {
  handleSelect: (client: TemplateBase) => void;
  handleClose: () => void;
}) {
  const { t } = useTranslate(["ProjectCreate", "Global"]);
  const { isMinDesktop } = useScreenWidth();

  const [selectedTemplate, setSelectedTemplate] =
    React.useState<TemplateBase | null>(null);

  const {
    queryBaseVariables,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    paginationModel,
    setPaginationModel,
    searchTerm,
    setSearchTerm,
  } = useTemplatesQueryVariablesInState({
    initialFilters: DEFAULT_FILTERS,
    initialSorting: searchQuotesSortingToQuoteTemplatesSorting(DEFAULT_SORTING),
    initialSearchTerm: "",
  });

  const sideTemplateSelect = (
    <TemplatesDisplay
      fixedFilters={{ applicableFor: ["QUOTE"] }}
      queryBaseVariables={queryBaseVariables}
      filters={filters}
      setFilters={setFilters}
      resetFilters={resetFilters}
      sorting={sorting}
      setSorting={setSorting}
      paginationModel={paginationModel}
      setPaginationModel={setPaginationModel}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      selectedTemplate={selectedTemplate}
      setSelectedTemplate={setSelectedTemplate}
      onInspectedTemplateClick={template => {
        handleSelect(template);
      }}
    />
  );
  return isMinDesktop ? (
    <>
      <ModalTitle
        handleClose={handleClose}
        title={t("Select template", { ns: "ProjectCreate" })}
      />
      <ModalContent>{sideTemplateSelect}</ModalContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <IconButton color="primary" onClick={handleClose}>
          <KeyboardDoubleArrowLeftIcon />
        </IconButton>
        <Button
          color="primary"
          variant="contained"
          disabled={!selectedTemplate}
          onClick={() => {
            if (!selectedTemplate) {
              throw new Error("No template selected");
            }
            handleSelect(selectedTemplate);
          }}
        >
          {t("Select", { ns: "Global" })}
        </Button>
      </DialogActions>
    </>
  ) : (
    <Modal
      handleClose={handleClose}
      title={t("Select template", { ns: "ProjectCreate" })}
      actionButtons={[
        {
          label: t("Cancel", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text" },
        },
        {
          label: t("Select", { ns: "Global" }),
          handleClick: () => {
            if (!selectedTemplate) {
              throw new Error("No template selected");
            }
            handleSelect(selectedTemplate);
          },
          buttonProps: { disabled: !selectedTemplate },
        },
      ]}
    >
      {sideTemplateSelect}
    </Modal>
  );
}

function TemplateWizardSidePanel({
  template,
  initialAnswers,
  handleClose,
  handleAnswer,
  handleComplete,
}: {
  template: TemplateBase;
  initialAnswers: ApplyItemAction[];
  handleAnswer: (result: question.AnsweredQuestion[]) => Promise<void> | void;
  handleComplete: () => void;
  handleClose: () => void;
}) {
  const viewer = useUserData().currentUser!;
  const { isMinDesktop } = useScreenWidth();

  const sideTemplateWizard = (
    <WizardSection
      onComplete={() => {
        handleComplete();
      }}
      onClose={handleClose}
      onAnswer={handleAnswer}
      initialAnswers={initialAnswers}
      wizardConfiguration={{
        sourceDoc: {
          type: "template",
          templateId: template.id,
          contractorId: viewer.organisation.id,
        },
        navigationType: null,
        questionStrategy: {
          type: "questionControl",
          askWhen: "onQuoteCreate",
          askWhom: "contractor",
          startingNode: { type: "root" },
        },
        requestInitialAnswers: "true",
        showCloseButton: "true",
      }}
    />
  );

  return isMinDesktop ? (
    <>
      <ModalContent contentProps={{ sx: { p: "8px !important" } }}>
        {sideTemplateWizard}
      </ModalContent>
    </>
  ) : (
    <ModalDialog handleClose={handleClose}>
      <ModalContent contentProps={{ sx: { p: "8px !important" } }}>
        {sideTemplateWizard}
      </ModalContent>
    </ModalDialog>
  );
}

function useProjectAssignToMe({
  projectId: propProjectId,
  refetchQueries,
}: {
  projectId?: string;
  refetchQueries?: string[];
}) {
  const viewer = useUserData().currentUser!;

  const client = useApolloClient();

  const [fetchQuery, query] = useProjectAssignToMeLazyQuery({
    client,
    variables: { projectId: propProjectId! },
    fetchPolicy: "network-only",
  });
  const [setAssignee, { loading: setAssigneeLoading }] =
    useProjectAssignToMe_SetProjectAssigneeMutation({
      client,
    });

  const assignToMe = async (passedProjectId?: string) => {
    const projectId = passedProjectId || propProjectId;
    if (!projectId) throw new Error("No project id provided");

    const result = await fetchQuery({ variables: { projectId } });
    const projectMembers =
      getDataOrNull(result.data?.project)?.project?.internalStakeholders ?? [];
    let membershipId =
      projectMembers.find(m => m.user.id === viewer.id)?.id ?? null;

    if (!membershipId) {
      throw new Error(
        "Failed to assign yourself to the project! You are not a member of the project"
      );
    }

    await setAssignee({
      variables: {
        projectId,
        membershipId,
      },
      refetchQueries,
      awaitRefetchQueries: true,
    });

    return { assigneeUserId: viewer.id };
  };

  return {
    assignToMe,
    loading: setAssigneeLoading || query.loading,
  };
}
